"use client";

import { useState } from "react";
import { motion } from "@/lib/motion";
import Link from "next/link";
import { useFormik } from "formik";
import { fieldTouchedError } from "@/lib/form-helpers";
import { betaTesterSchema } from "@/lib/form-schemas";
import {
  CheckCircle2,
  Zap,
  Megaphone,
  Gift,
  FlaskConical,
  Send,
  ChevronRight,
} from "@/components/icons";
import { HeroGlow, FormError, FormSuccess, brandActionClasses } from "@/components/ui";
import { EMAIL } from "@/lib/constants";
import { cn } from "@/lib/utils";

const PERKS = [
  {
    icon: Zap,
    title: "Early access",
    description: "Try new features weeks before they ship publicly.",
  },
  {
    icon: Megaphone,
    title: "Shape the roadmap",
    description: "Your feedback directly influences what we build next.",
  },
  {
    icon: Gift,
    title: "Exclusive rewards",
    description: "Beta testers receive special discounts and perks.",
  },
  {
    icon: FlaskConical,
    title: "Direct line to the team",
    description: "Communicate with Proxidize engineers and PMs directly.",
  },
];

const START_OPTIONS = [
  "Just started (less than 1 month)",
  "1–3 months ago",
  "3–6 months ago",
  "6–12 months ago",
  "More than a year ago",
  "I haven't started yet — evaluating now",
];

const USE_CASES = [
  "Mobile proxies / 4G proxies",
  "Web scraping & data collection",
  "Social media monitoring",
  "Ad verification",
  "Market research",
  "Other",
];

const TECHNICAL_LEVELS = [
  "Non-technical (business user)",
  "Some technical knowledge",
  "Developer / engineer",
  "DevOps / infrastructure engineer",
];

export default function BetaTestersSection() {
  const [submitted, setSubmitted] = useState(false);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      startedUsing: "",
      useCase: "",
      technicalLevel: "",
      featureRequests: "",
    },
    validationSchema: betaTesterSchema,
    onSubmit: () => setSubmitted(true),
  });

  return (
    <main id="main-content" tabIndex={-1} className="relative overflow-y-clip pb-32">
      <HeroGlow align="center" />

      <div className="relative mx-auto max-w-7xl px-4 pt-32 pb-16 sm:px-6 lg:px-8">
        {/* ── Page header ─────────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto mb-16 max-w-2xl text-center"
        >
          <h1 className="text-foreground mb-5 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl">
            Proxidize Beta Testers Program
          </h1>
          <p className="text-muted-foreground text-base leading-relaxed sm:text-lg">
            Get early access to the latest Proxidize features before they go live. Help us test,
            improve, and shape the future of mobile proxy infrastructure.
          </p>
        </motion.div>

        {/* ── Perks row ───────────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 grid grid-cols-2 gap-2 sm:gap-4 lg:grid-cols-4"
        >
          {PERKS.map((perk, i) => (
            <motion.div
              key={perk.title}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.4,
                delay: 0.15 + i * 0.07,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="border-border bg-card rounded-xl border p-4 sm:p-5"
            >
              <div
                className="mb-3 flex h-8 w-8 items-center justify-center rounded-lg"
                style={{ backgroundColor: "rgba(91,140,255,0.08)" }}
              >
                <perk.icon size={15} style={{ color: "var(--brand-green)" }} />
              </div>
              <p className="text-foreground mb-1 text-sm font-semibold">{perk.title}</p>
              <p className="text-muted-foreground text-xs leading-relaxed">{perk.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* ── Two-column: info + form ──────────────────────────────────────── */}
        <div className="mx-auto grid max-w-5xl items-start gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-16">
          {/* Left column */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 className="text-foreground mb-4 text-xl font-bold">Who should apply?</h2>
            <ul className="mb-8 space-y-3">
              {[
                "Existing Proxidize users of any plan",
                "Developers building on the Proxidize API",
                "Teams running large-scale proxy operations",
                "Researchers and data engineers",
                "Anyone curious about next-gen proxy tech",
              ].map((item, i) => (
                <motion.li
                  key={item}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    duration: 0.4,
                    delay: 0.25 + i * 0.06,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="text-foreground flex items-start gap-2.5 text-sm"
                >
                  <CheckCircle2
                    size={15}
                    className="mt-0.5 shrink-0"
                    style={{ color: "var(--brand-green)" }}
                  />
                  {item}
                </motion.li>
              ))}
            </ul>

            <div className="border-border bg-card mb-8 rounded-xl border p-5">
              <p className="text-muted-foreground mb-2 text-xs font-semibold">What happens next?</p>
              <ol className="space-y-2.5">
                {[
                  "Submit your application below",
                  "Our team reviews within 3 business days",
                  "Accepted testers get onboarding instructions",
                  "You gain access to the beta dashboard",
                ].map((step, i) => (
                  <li key={step} className="text-muted-foreground flex items-start gap-2.5 text-sm">
                    <span
                      className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
                      style={{
                        backgroundColor: "rgba(91,140,255,0.12)",
                        color: "var(--brand-green)",
                      }}
                    >
                      {i + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>

            <div className="border-border border-t pt-6">
              <p className="text-muted-foreground mb-2 text-sm">Already a Proxidize customer?</p>
              <Link
                href="/contact/support"
                className="text-foreground hover:text-primary inline-flex items-center gap-1 text-sm font-semibold transition-colors duration-200"
              >
                Contact support
                <ChevronRight size={14} />
              </Link>
            </div>
          </motion.div>

          {/* Right column — form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            {submitted ? (
              <FormSuccess
                title="Application submitted!"
                description="Thanks for applying to the Proxidize Beta Testers Program. We'll review your application and get back to you within 3 business days."
              >
                <Link
                  href="/"
                  className="text-foreground mt-2 inline-flex items-center gap-1 text-sm font-semibold transition-opacity hover:opacity-80"
                >
                  ← Back to home
                </Link>
              </FormSuccess>
            ) : (
              <form
                onSubmit={formik.handleSubmit}
                className="border-border bg-card space-y-5 rounded-2xl border p-6 sm:p-8"
                noValidate
              >
                <div className="border-border border-b pb-4">
                  <h2 className="text-foreground text-base font-semibold">
                    Apply to the beta program
                  </h2>
                  <p className="text-muted-foreground mt-1 text-xs">
                    All fields marked * are required.
                  </p>
                </div>

                {/* First name + Last name */}
                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                  <div className="space-y-1.5">
                    <label
                      htmlFor="firstName"
                      className="text-muted-foreground block text-xs font-medium"
                    >
                      First name *
                    </label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      autoComplete="given-name"
                      placeholder="Jane"
                      value={formik.values.firstName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="border-border bg-background text-foreground placeholder:text-muted-foreground focus:ring-primary/40 w-full rounded-lg border px-3 py-2.5 text-sm transition-shadow focus:ring-2 focus:outline-none"
                    />
                    {fieldTouchedError(formik, "firstName") && (
                      <FormError>{fieldTouchedError(formik, "firstName")}</FormError>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <label
                      htmlFor="lastName"
                      className="text-muted-foreground block text-xs font-medium"
                    >
                      Last name *
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      autoComplete="family-name"
                      placeholder="Smith"
                      value={formik.values.lastName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="border-border bg-background text-foreground placeholder:text-muted-foreground focus:ring-primary/40 w-full rounded-lg border px-3 py-2.5 text-sm transition-shadow focus:ring-2 focus:outline-none"
                    />
                    {fieldTouchedError(formik, "lastName") && (
                      <FormError>{fieldTouchedError(formik, "lastName")}</FormError>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-1.5">
                  <label
                    htmlFor="email"
                    className="text-muted-foreground block text-xs font-medium"
                  >
                    Email address *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="jane@company.com"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="border-border bg-background text-foreground placeholder:text-muted-foreground focus:ring-primary/40 w-full rounded-lg border px-3 py-2.5 text-sm transition-shadow focus:ring-2 focus:outline-none"
                  />
                  {fieldTouchedError(formik, "email") && (
                    <FormError>{fieldTouchedError(formik, "email")}</FormError>
                  )}
                </div>

                {/* When did you start using Proxidize */}
                <div className="space-y-1.5">
                  <label
                    htmlFor="startedUsing"
                    className="text-muted-foreground block text-xs font-medium"
                  >
                    When did you start using Proxidize? *
                  </label>
                  <div className="relative">
                    <select
                      id="startedUsing"
                      name="startedUsing"
                      value={formik.values.startedUsing}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="border-border bg-background text-foreground focus:ring-primary/40 w-full cursor-pointer appearance-none rounded-lg border px-3 py-2.5 text-sm transition-shadow focus:ring-2 focus:outline-none"
                    >
                      <option value="" disabled>
                        Select…
                      </option>
                      {START_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    <div className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M2 4l4 4 4-4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                  {fieldTouchedError(formik, "startedUsing") && (
                    <FormError>{fieldTouchedError(formik, "startedUsing")}</FormError>
                  )}
                </div>

                {/* Primary use case */}
                <div className="space-y-1.5">
                  <label
                    htmlFor="useCase"
                    className="text-muted-foreground block text-xs font-medium"
                  >
                    Primary use case *
                  </label>
                  <div className="relative">
                    <select
                      id="useCase"
                      name="useCase"
                      value={formik.values.useCase}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="border-border bg-background text-foreground focus:ring-primary/40 w-full cursor-pointer appearance-none rounded-lg border px-3 py-2.5 text-sm transition-shadow focus:ring-2 focus:outline-none"
                    >
                      <option value="" disabled>
                        Select…
                      </option>
                      {USE_CASES.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    <div className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M2 4l4 4 4-4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                  {fieldTouchedError(formik, "useCase") && (
                    <FormError>{fieldTouchedError(formik, "useCase")}</FormError>
                  )}
                </div>

                {/* Technical level */}
                <div className="space-y-1.5">
                  <label
                    htmlFor="technicalLevel"
                    className="text-muted-foreground block text-xs font-medium"
                  >
                    Technical background
                  </label>
                  <div className="relative">
                    <select
                      id="technicalLevel"
                      name="technicalLevel"
                      value={formik.values.technicalLevel}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="border-border bg-background text-foreground focus:ring-primary/40 w-full cursor-pointer appearance-none rounded-lg border px-3 py-2.5 text-sm transition-shadow focus:ring-2 focus:outline-none"
                    >
                      <option value="" disabled>
                        Select…
                      </option>
                      {TECHNICAL_LEVELS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    <div className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M2 4l4 4 4-4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Feature requests */}
                <div className="space-y-1.5">
                  <label
                    htmlFor="featureRequests"
                    className="text-muted-foreground block text-xs font-medium"
                  >
                    What features or improvements would you like to see?
                  </label>
                  <textarea
                    id="featureRequests"
                    name="featureRequests"
                    rows={4}
                    placeholder="Tell us what you'd love to test or what's missing for your workflow…"
                    value={formik.values.featureRequests}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="border-border bg-background text-foreground placeholder:text-muted-foreground focus:ring-primary/40 w-full resize-none rounded-lg border px-3 py-2.5 text-sm transition-shadow focus:ring-2 focus:outline-none"
                  />
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4 pt-1">
                  <p className="text-muted-foreground text-xs">
                    Questions?{" "}
                    <a
                      href={`mailto:${EMAIL.support}`}
                      className="text-foreground hover:text-primary font-medium transition-colors"
                    >
                      {EMAIL.support}
                    </a>
                  </p>
                  <button
                    type="submit"
                    className={cn(
                      brandActionClasses,
                      "inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors"
                    )}
                  >
                    Apply now
                    <Send size={13} />
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </main>
  );
}
